import { useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LeftMenu,
  AdminLeftMenu,
  OrgAdminLeftMenu,
  StudentPatientLeftMenu,
  SchoolAdminLeftMenu,
  TeacherLeftMenu,
} from '../../constants/LeftMenu';
import { UIContext } from '../../contexts/UIProvider';
import { LogAnalyticsEvent } from '../../utils/firebase/firebase';
import { ANALYTICS_KEYS } from '../../constants/AnalyticKeys';
import AuthContext from '../../contexts/AuthContext';

export const useLeftPanel = (userType) => {
  const { user, typeOfUser, setTypeOfUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    selectedMenuKey,
    updateSelectedMenu,
    setIsDrawerOpen,
    isNewMesage,
    isDrawerExpanded,
    setIsDrawerExpanded,
    isHovering,
    setIsHovering,
  } = useContext(UIContext);

  const menuItemsToRender = useMemo(() => {
    switch (userType) {
      case 'ADMIN':
        return AdminLeftMenu;
      case 'ORGANIZATIONADMIN':
        return OrgAdminLeftMenu;
      case 'SCHOOLADMIN':
        return SchoolAdminLeftMenu;
      case 'STUDENT':
        return StudentPatientLeftMenu;
      case 'PATIENT':
        return StudentPatientLeftMenu;
      case 'TEACHER':
        return TeacherLeftMenu;
      default:
        return LeftMenu;
    }
  }, [userType]);

  const handleMenuClick = useCallback(
    (item) => {
      updateSelectedMenu(item.key);
      item.path && navigate(item.path);
      setIsDrawerOpen(false);

      // Log the analytics event for the left menubar
      LogAnalyticsEvent(ANALYTICS_KEYS.LEFTMENU.SIDEBARMENUCLICK, {
        page_name: item.name,
        page_key: item.key,
      });
    },
    [updateSelectedMenu, navigate, setIsDrawerOpen]
  );

  const handleRoleToggle = useCallback((newRole) => {
    setTypeOfUser(newRole);
    localStorage.setItem('selectedRole', newRole);
    
    // Navigate to appropriate dashboard based on role
    switch (newRole) {
      case 'SCHOOLADMIN':
        navigate('/schoolAdmin');
        break;
      case 'TEACHER':
        navigate('/');
        break;
      case 'ADMIN':
        navigate('/admin');
        break;
      case 'ORGANIZATIONADMIN':
        navigate('/organizationAdmin');
        break;
      default:
        navigate('/');
        break;
    }
  }, [setTypeOfUser, navigate]);

  const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);
  const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

  const drawerWidth = isHovering || isDrawerExpanded ? 280 : 70;

  // Initialize drawer state
  useEffect(() => {
    if (isDrawerExpanded === undefined) {
      setIsDrawerExpanded(false);
    }
  }, [isDrawerExpanded, setIsDrawerExpanded]);

  useEffect(() => {
    if (isHovering === undefined) {
      setIsHovering(false);
    }
  }, [isHovering, setIsHovering]);

  return {
    user,
    typeOfUser,
    selectedMenuKey,
    isNewMesage,
    isDrawerExpanded,
    isHovering,
    menuItemsToRender,
    drawerWidth,
    handleMenuClick,
    handleRoleToggle,
    handleMouseEnter,
    handleMouseLeave,
    setIsDrawerExpanded,
  };
}; 